import { useEffect, useRef } from 'react'
import { matchRoutes, useLocation } from 'react-router'
import mixpanelBrowser from 'mixpanel-browser/src/loaders/loader-module-core'
import type { Mixpanel } from './mixpanel'

const allowedProperties = new Set([
    'token',
    'distinct_id',
    '$device_id',
    '$insert_id',
    'time',
    'mp_lib',
    '$lib_version',
    '$os',
    '$browser',
    '$browser_version',
    '$device',
    '$screen_height',
    '$screen_width',
    'source',
    'role',
    'path',
    'component',
    'action',
    'to',
    'search_keys',
    'method',
    'success',
])

/** Creates isolated, anonymous portal telemetry using route templates instead of customer URLs. */
export function createPortalMixpanel(options: { token?: string; source: string; paths: string[] }) {
    const routes = options.paths.map((path) => ({ path }))
    let initialized = false
    let role: string | number | undefined
    const routePath = (url: string) => {
        if (!url.startsWith('/') || url.startsWith('//')) return 'external'
        return matchRoutes(routes, url.split(/[?#]/)[0])?.[0]?.route.path ?? 'unknown'
    }
    const track = (event: string, properties: Record<string, unknown>) => {
        if (!initialized) return
        mixpanelBrowser.track(event, { ...properties, source: options.source, role })
    }
    return {
        init() {
            if (initialized || !options.token || navigator.webdriver) return
            mixpanelBrowser.init(options.token, {
                api_host: 'https://api-eu.mixpanel.com',
                persistence: 'localStorage',
                persistence_name: options.source,
                cross_subdomain_cookie: false,
                ip: false,
                autocapture: false,
                track_pageview: false,
                record_sessions_percent: 0,
                save_referrer: false,
                stop_utm_persistence: true,
                skip_first_touch_marketing: true,
                // The SDK adds the current URL and referrer even to manually tracked events.
                hooks: {
                    before_send_events: (event) => ({
                        ...event,
                        properties: Object.fromEntries(
                            Object.entries(event.properties).filter(([key]) => allowedProperties.has(key))
                        ),
                    }),
                },
                property_blacklist: ['$current_url', '$referrer', '$initial_referrer', '$initial_referring_domain'],
            })
            initialized = true
        },
        setRole(value: string | number | undefined) {
            role = value
        },
        reset() {
            role = undefined
            if (initialized) mixpanelBrowser.reset()
        },
        trackPage(path: string) {
            track('Viewed Page', { path: routePath(path) })
        },
        trackLogin(method: 'password' | 'passkey') {
            track('Login', { method, success: true })
        },
        trackClickLink({ component, to }: { component: string; to: string }) {
            track('Clicked Link', { component, to: routePath(to) })
        },
        trackClickComponent({ component, action }: Record<string, unknown> & { component: string }) {
            track('Clicked Component', { component, action })
        },
        trackSearchResource({ component, search_keys }: { component: string; search_keys: string[] }) {
            track('Searched Resource', { component, search_keys })
        },
    } satisfies Mixpanel & Record<string, unknown>
}

/** Tracks the initial page and pathname changes without duplicating StrictMode effects. */
export function usePortalPageTracking(
    analytics: ReturnType<typeof createPortalMixpanel>,
    role: string | number | undefined,
    ready = true
) {
    const { pathname } = useLocation()
    const previousPath = useRef<string | undefined>(undefined)
    useEffect(() => {
        if (!ready) return
        analytics.setRole(role)
        if (previousPath.current === pathname) return
        previousPath.current = pathname
        analytics.trackPage(pathname)
    }, [analytics, pathname, role, ready])
}
